import React from 'react';
import { CalendarBlank } from '@phosphor-icons/react';

// Analytics API row shape from learn_my_active_courses metric (filtered by due_date)
interface ActiveCourseRow {
    id: string | number;
    courseTitle: string;
    dueDate?: string;
    progressPercent?: number;
    assignmentCategory?: string;
    progressStatus: string;
}

interface UpcomingDeadlinesWidgetProps {
    data?: ActiveCourseRow[] | null;
    loading?: boolean;
    error?: string;
}

const getDueStatus = (dueDate?: string) => {
    if (!dueDate) return { dot: 'bg-gray-300', bg: 'hover:bg-gray-50', label: '', className: 'text-gray-400' };
    const now = new Date();
    const due = new Date(dueDate);
    const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return {
        dot: 'bg-red-500', bg: 'bg-red-50', className: 'text-red-500',
        label: `Overdue by ${Math.abs(diffDays)} days`,
    };
    if (diffDays <= 7) return {
        dot: 'bg-amber-500', bg: 'bg-amber-50', className: 'text-amber-600',
        label: `Due in ${diffDays} days`,
    };
    const formatted = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return { dot: 'bg-gray-300', bg: 'hover:bg-gray-50', className: 'text-gray-400', label: `Due ${formatted}` };
};

const getCategoryBadge = (category?: string) => {
    if (category?.toLowerCase() === 'mandatory') return { label: 'Mandatory', className: 'bg-red-100 text-red-700' };
    return { label: 'Optional', className: 'bg-blue-100 text-blue-700' };
};

export const UpcomingDeadlinesWidget: React.FC<UpcomingDeadlinesWidgetProps> = ({ data, loading, error }) => {
    const courses = Array.isArray(data) ? data : [];

    // Filter to items with due dates, sort by soonest
    const deadlineItems = courses
        .filter(c => c.dueDate && c.progressStatus !== 'completed')
        .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime());

    const totalCount = deadlineItems.length;

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 pt-4 pb-4 flex items-center justify-between">
                {totalCount > 0 && (
                    <span className="min-w-[20px] h-5 text-[11px] bg-red-100 text-red-600 rounded-full flex items-center justify-center font-semibold px-1.5">
                        {totalCount}
                    </span>
                )}
            </div>
            <div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : deadlineItems.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <CalendarBlank className="w-8 h-8" />
                        <span className="text-sm">No upcoming deadlines</span>
                    </div>
                ) : (
                    <div className="space-y-2.5">
                        {deadlineItems.map((item) => {
                            const due = getDueStatus(item.dueDate);
                            const badge = getCategoryBadge(item.assignmentCategory);
                            const progress = Number(item.progressPercent) || 0;
                            return (
                                <div key={item.id} className={`flex items-center gap-3 p-2.5 rounded-lg ${due.bg}`}>
                                    <div className={`w-2 h-2 rounded-full ${due.dot} shrink-0`} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-gray-800 truncate">{item.courseTitle}</div>
                                        <div className={`text-xs mt-0.5 ${due.className}`}>
                                            {due.label}
                                            {progress > 0 && ` · ${progress}% complete`}
                                        </div>
                                    </div>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${badge.className}`}>
                                        {badge.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
